import React from "react";
import { cn } from "@/utils/cn";

type CodeInputProps = {
  value: string;
  onChange: (value: string) => void;
  /** Called once all six digits are entered, typed or pasted. */
  onComplete: (value: string) => void;
  invalid: boolean;
  disabled: boolean;
};

/**
 * Six boxes drawn over one input, so browser autofill, paste, and screen readers all see a
 * single one-time-code field. Codes always read left to right.
 */
export function CodeInput({ value, onChange, onComplete, invalid, disabled }: CodeInputProps) {
  const [focused, setFocused] = React.useState(false);
  return (
    <div
      dir="ltr"
      className={`relative grid grid-cols-6 gap-2 ${invalid ? "motion-safe:animate-[s-login-shake_.35s]" : ""}`}
    >
      {Array.from({ length: 6 }, (_, index) => {
        const active = focused && index === Math.min(value.length, 5);
        return (
          <span
            key={index}
            aria-hidden="true"
            className={cn(
              "grid h-11 place-items-center rounded-lg border text-xl font-label tabular-nums transition-[border-color,box-shadow] dark:bg-input/30",
              invalid ? "border-destructive" : active ? "border-ring" : "border-input",
              active && "ring-3 ring-ring/50",
            )}
          >
            {value[index] ??
              (active ? (
                <span className="h-[18px] w-[1.5px] bg-foreground motion-safe:animate-[s-login-blink_1s_steps(1)_infinite]" />
              ) : null)}
          </span>
        );
      })}
      <input
        className="absolute inset-0 h-full w-full cursor-text opacity-0"
        aria-label="Six-digit code"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={value}
        disabled={disabled}
        autoFocus
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(event) => {
          const digits = event.target.value.replace(/\D/g, "").slice(0, 6);
          onChange(digits);
          if (digits.length === 6) onComplete(digits);
        }}
      />
    </div>
  );
}
